// Reusable guided product tour engine for the vendor portal.
// Mount <TourProvider> in the portal shell; descendants call useTour().
// Tour configs live in ./registry.ts. See docs/vendor-portal-tours.md.
// Rendering is delegated to the shared SpotlightOverlay in ./tour-engine.tsx.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { SpotlightOverlay } from "./tour-engine";
import { TOUR_REGISTRY, type TourConfig, type TourPlacement, type TourStep } from "./registry";

// Minimum gap (ms) between two step transitions. Guards against duplicate
// nextStep/prevStep calls within a single tick — e.g. the SpotlightOverlay's
// missing-target auto-advance racing a manual Next click, which previously
// made one click look like two step advances.
const ADVANCE_DEBOUNCE_MS = 300;

// Re-export types so existing consumers (registry, TourButton, etc.) don't break.
export type { TourConfig, TourPlacement, TourStep };

// ─── Types ──────────────────────────────────────────────────────────────────

interface TourProgressRecord {
  status: "in_progress" | "completed" | "skipped";
  lastStepIndex: number;
  completedAt: string | null;
  skippedAt: string | null;
}

type TourProgressMap = Record<string, TourProgressRecord>;

interface TourContextValue {
  activeTour: TourConfig | null;
  stepIndex: number;
  progress: TourProgressMap;
  isProgressLoading: boolean;
  hasTour: (tourId: string | null | undefined) => boolean;
  startTour: (tourId: string) => void;
  stopTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  skipTour: () => void;
}

const TourContext = createContext<TourContextValue | null>(null);

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error("useTour must be used inside <TourProvider>");
  return ctx;
}

// ─── Provider ───────────────────────────────────────────────────────────────

interface TourProviderProps {
  enabled?: boolean;
  children: React.ReactNode;
  /**
   * Tour registry powering this provider. Defaults to the vendor-portal
   * `TOUR_REGISTRY` for backward compatibility; the partner portal passes its
   * own `PARTNER_TOUR_REGISTRY` so the same engine drives a different surface.
   */
  registry?: Record<string, TourConfig>;
  /**
   * Base path for the tour-progress REST endpoints (GET list + POST
   * start/progress/complete/skip). Defaults to the vendor endpoints; the
   * partner portal passes `/api/partner/tours`.
   */
  apiBase?: string;
}

export function TourProvider({
  enabled = true,
  children,
  registry = TOUR_REGISTRY,
  apiBase = "/api/vendor-portal/tours",
}: TourProviderProps) {
  const queryClient = useQueryClient();
  const [activeTourId, setActiveTourId] = useState<string | null>(null);
  const [stepIndex, setStepIndex] = useState(-1);
  // True when replaying a tour the user already completed/skipped. In that
  // mode the engine runs the UI but does not mutate the persisted record.
  const replayModeRef = useRef(false);
  const lastAdvanceRef = useRef(0);

  const { data: progress = {}, isLoading: isProgressLoading } = useQuery<TourProgressMap>({
    queryKey: [apiBase],
    enabled,
  });

  const activeTour = activeTourId ? registry[activeTourId] ?? null : null;

  const persistStart = useCallback(async (tourId: string) => {
    try {
      await apiRequest("POST", `${apiBase}/${encodeURIComponent(tourId)}/start`);
      queryClient.invalidateQueries({ queryKey: [apiBase] });
    } catch (e) {
      console.warn("[tour] failed to persist start:", e);
    }
  }, [queryClient, apiBase]);

  const persistProgress = useCallback(async (tourId: string, idx: number) => {
    try {
      await apiRequest("POST", `${apiBase}/${encodeURIComponent(tourId)}/progress`, { stepIndex: idx });
    } catch (e) {
      console.warn("[tour] failed to persist progress:", e);
    }
  }, [apiBase]);

  const persistComplete = useCallback(async (tourId: string) => {
    try {
      await apiRequest("POST", `${apiBase}/${encodeURIComponent(tourId)}/complete`);
      queryClient.invalidateQueries({ queryKey: [apiBase] });
    } catch (e) {
      console.warn("[tour] failed to persist complete:", e);
    }
  }, [queryClient, apiBase]);

  const persistSkip = useCallback(async (tourId: string) => {
    try {
      await apiRequest("POST", `${apiBase}/${encodeURIComponent(tourId)}/skip`);
      queryClient.invalidateQueries({ queryKey: [apiBase] });
    } catch (e) {
      console.warn("[tour] failed to persist skip:", e);
    }
  }, [queryClient, apiBase]);

  const startTour = useCallback((tourId: string) => {
    if (!registry[tourId]) {
      console.warn(`[tour] no registered tour with id "${tourId}"`);
      return;
    }
    const existing = progress[tourId];
    const isReplay = !!(existing && (existing.status === "completed" || existing.status === "skipped"));
    replayModeRef.current = isReplay;
    setActiveTourId(tourId);
    setStepIndex(0);
    // Reset (not stamp) so the first advance — including a missing-target
    // auto-advance from the SpotlightOverlay — is never blocked.
    lastAdvanceRef.current = 0;
    if (!isReplay) void persistStart(tourId);
  }, [persistStart, progress]);

  const stopTour = useCallback(() => {
    setActiveTourId(null);
    setStepIndex(-1);
    replayModeRef.current = false;
    lastAdvanceRef.current = 0;
  }, []);

  const finishTour = useCallback(() => {
    if (activeTourId && !replayModeRef.current) void persistComplete(activeTourId);
    stopTour();
  }, [activeTourId, persistComplete, stopTour]);

  const skipTour = useCallback(() => {
    if (activeTourId && !replayModeRef.current) void persistSkip(activeTourId);
    stopTour();
  }, [activeTourId, persistSkip, stopTour]);

  const nextStep = useCallback(() => {
    if (!activeTour) return;
    const now = Date.now();
    if (now - lastAdvanceRef.current < ADVANCE_DEBOUNCE_MS) return;
    lastAdvanceRef.current = now;
    if (stepIndex >= activeTour.steps.length - 1) {
      finishTour();
      return;
    }
    const next = stepIndex + 1;
    setStepIndex(next);
    if (activeTourId && !replayModeRef.current) void persistProgress(activeTourId, next);
  }, [activeTour, activeTourId, stepIndex, finishTour, persistProgress]);

  const prevStep = useCallback(() => {
    if (!activeTour || stepIndex <= 0) return;
    const now = Date.now();
    if (now - lastAdvanceRef.current < ADVANCE_DEBOUNCE_MS) return;
    lastAdvanceRef.current = now;
    const prev = stepIndex - 1;
    setStepIndex(prev);
    if (activeTourId && !replayModeRef.current) void persistProgress(activeTourId, prev);
  }, [activeTour, activeTourId, stepIndex, persistProgress]);

  const hasTour = useCallback((tourId: string | null | undefined) => {
    if (!tourId) return false;
    return Boolean(registry[tourId]);
  }, [registry]);

  // Esc = skip, ←/→ = back/next.
  useEffect(() => {
    if (!activeTour) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); skipTour(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); nextStep(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); prevStep(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [activeTour, nextStep, prevStep, skipTour]);

  const value = useMemo<TourContextValue>(() => ({
    activeTour,
    stepIndex,
    progress,
    isProgressLoading,
    hasTour,
    startTour,
    stopTour,
    nextStep,
    prevStep,
    skipTour,
  }), [activeTour, stepIndex, progress, isProgressLoading, hasTour, startTour, stopTour, nextStep, prevStep, skipTour]);

  return (
    <TourContext.Provider value={value}>
      {children}
      {activeTour && stepIndex >= 0 && (
        <SpotlightOverlay
          tour={activeTour}
          stepIndex={stepIndex}
          onNext={nextStep}
          onPrev={prevStep}
          onSkip={skipTour}
          onClose={skipTour}
          testIdPrefix="tour"
          actionButtonClass="bg-secondary text-secondary-foreground hover:bg-secondary/90"
          skipLabel="Skip tour"
        />
      )}
    </TourContext.Provider>
  );
}
